import firebase from 'firebase/app';

import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { QueryParams, UrlParams } from '../../../../../types/urlParams';

import AddOrEditRoomDialog from '../../../../../components/AddOrEditRoomDialog';
import AssetIdSearcher from '../../../../../components/AssetIdSearcher';
import EditFloorDialog from '../../../../../components/EditFloorDialog';
import ErrorBoundary from '../../../../../components/ErrorBoundary';
import { useModal } from '../../../../../components/Modal';
import Table from '../../../../../components/Table';
import TableActionButton from '../../../../../components/TableActionButton';
import { PORTAL_ROLES } from '../../../../../config/roles';
import { AppState, useAppDispatch } from '../../../../../config/store';
import { useAllRoomsForFloor } from '../../../../../hooks/useAllRooms';
import useAuthChecker from '../../../../../hooks/useAuthChecker';
import useQueryParams from '../../../../../hooks/useQueryParams';
import useQueryParamsUpdater from '../../../../../hooks/useQueryParamsUpdater';
import useSurvey from '../../../../../hooks/useSurvey';
import { EMPTY_ARRAY } from '../../../../../shared/helpers';
import { getLastUpdated } from '../../../../../shared/logTools';
import { onSaveFloor } from '../../../../../state/floor';
import { setParamId } from '../../../../../state/params';
import { onSaveRoom } from '../../../../../state/room';
import { setLoading, setSnackMessage } from '../../../../../state/ui';
import {
  RoomProps,
  type RoomScheduleRoomProps,
} from '../../../../../types/room';
import { Statuses } from '../../../../../types/status';
import ViewFloorPlanDialog from '../../components/ViewFloorPlanDialog';
import { EMPTY_ROOM, TABLE_COLUMNS } from './constants';
import exportRoomSchedule from './exportRoomSchedule';
import { BigBatch } from '@qualdesk/firestore-big-batch';
import { canViewMultipleSurveyors } from '../../../../../shared/auth';
import { getFirestore } from 'redux-firestore';
import { useSelector } from 'react-redux';

const getRoomScheduleMatchKey = (
  room: Pick<RoomScheduleRoomProps, 'floor' | 'name'>,
) =>
  `${String(room?.floor || '')
    .trim()
    .toLowerCase()}::${String(room?.name || '')
    .trim()
    .toLowerCase()}`;

const isNotDeleted = (deleted?: number | null) =>
  deleted == null || Number(deleted) === 0;

const sortRoomsByLastUpdated = (a, b) =>
  (b?.lastUpdated?.time || 0) - (a?.lastUpdated?.time || 0);
const Rooms = () => {
  const firestore = getFirestore(firebase);
  const history = useHistory();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { fp, floor, page, perpage } = useQueryParams<QueryParams>();
  const { updateQueryParams } = useQueryParamsUpdater();
  const { clientId, surveyId } = useParams<UrlParams>();

  const prevFloorName = useRef<string>();
  const [showFloorPlan, setShowFloorPlan] = useState(fp || false);
  const [roomToAddOrEdit, setRoomToAddOrEdit] = useState<
    undefined | Partial<RoomProps>
  >(undefined);
  const [floorToEdit, setFloorToEdit] = useState<string>();
  const survey = useSurvey();
  const { showModal } = useModal();
  const userRoles = useSelector(
    (state: AppState) => state.firebase.profile.token?.claims?.roles,
  );
  const canViewMultiSurveyorUi = canViewMultipleSurveyors({
    roles: userRoles,
    clientId,
  });
  const { checkAuth, isChecking: isCheckingAuth } = useAuthChecker({
    atLeast: PORTAL_ROLES.SURVEYOR,
  });

  const floors = useMemo(() => {
    return survey?.roomSchedule?.floors || EMPTY_ARRAY;
  }, [survey]);

  const floorParamAsName = useMemo(() => {
    if (!floors.length) {
      return undefined;
    }
    const floorIndex =
      typeof floor === 'number' && Number.isInteger(floor) && floor >= 0
        ? floor
        : 0;
    return floors[floorIndex];
  }, [floor, floors]);

  const floorName = floorParamAsName || floors[0];

  const { allRooms: allRoomsForFloor, isLoading: isRoomsLoading } =
    useAllRoomsForFloor(floorName, sortRoomsByLastUpdated);

  const floorPlan = useMemo(() => {
    return survey.floorPlans?.find((fp) => fp.floor === floorName);
  }, [survey, floorName]);

  useEffect(() => {
    if (!floorName || prevFloorName.current === floorName) {
      return;
    }
    prevFloorName.current = floorName;
    dispatch(
      setParamId({
        id: 'floor',
        value: floorName,
      }),
    );
  }, [dispatch, floorName]);

  useEffect(() => {
    const tableContainer = document.querySelector(
      '#main-content .MuiTableContainer-root',
    );
    if (tableContainer instanceof HTMLElement) {
      tableContainer.scrollTop = 0;
    }
  }, [page, perpage, floorName]);

  const rows = useMemo(() => {
    return allRoomsForFloor.map((room: RoomProps) => {
      const {
        id,
        name,
        floor,
        status,
        assetsNumCreated,
        assetsNumDeleted,
        partialsNumCreated,
        partialsNumDeleted,
        assetsNumFlagged,
        areaFloor,
        areaWall,
        lastUpdated,
        surveyorStatus,
      } = room;
      const totalAssets = assetsNumCreated - assetsNumDeleted;

      return {
        id,
        name,
        floor,
        image: room?.images?.[0]?.uri,
        lastUpdated,
        areaFloor: areaFloor.toFixed(2),
        areaWall: areaWall.toFixed(2),
        assets: totalAssets || 0,
        partials: partialsNumCreated - partialsNumDeleted || 0,
        flagged: assetsNumFlagged || 0,
        status: status || 'N/A',
        surveyorStatus: surveyorStatus || {},
        surveyors: survey?.surveyors || {},
        canViewMultipleSurveyors: canViewMultiSurveyorUi,
      };
    });
  }, [allRoomsForFloor, canViewMultiSurveyorUi, survey?.surveyors]);

  const onClickExportRoomSchedule = async () => {
    exportRoomSchedule({ clientId: clientId, survey });
  };

  const onToggleFloorPlan = () => {
    updateQueryParams({ fp: !showFloorPlan });
    setShowFloorPlan(!showFloorPlan);
  };

  const onClickedViewRoom = useCallback(
    (roomData) => {
      history.push(`${location.pathname}/${roomData.id}`);
    },
    [history, location.pathname],
  );

  const onClickedEditRoom = useCallback(
    async (roomData) => {
      const room = await firestore
        .collection('clients')
        .doc(clientId)
        .collection('surveys')
        .doc(surveyId)
        .collection('rooms')
        .doc(roomData.id)
        .get();

      setRoomToAddOrEdit((room.data() as RoomProps) || roomData);
    },
    [clientId, surveyId],
  );

  const onClickedDeleteRoom = useCallback(
    async (roomData: RoomScheduleRoomProps) => {
      if (survey.status !== Statuses.COMPLETED) {
        return showModal({
          title: 'Cannot Delete Room',
          messages: [
            `Rooms can only be deleted from surveys with a status of Completed. The current survey status is ${survey.status}. Please change the survey status to Completed to delete rooms.`,
          ],
          buttons: {
            confirm: [{ text: 'Ok' }],
          },
        });
      }

      const deleteConfirmed = await showModal({
        title: 'Please Confirm',
        buttons: {
          confirm: [{ text: 'Yes', value: true }],
          cancel: [{ text: 'No' }],
        },
        messages: [
          `Deleting ${roomData.name} will also delete all assets collected within it. Are you sure you want to continue?`,
        ],
      });
      if (!deleteConfirmed) {
        return;
      }

      try {
        dispatch(
          setLoading({
            loadingMessage: `Deleting ${roomData.name}, please wait...`,
          }),
        );
        const deletedAt = Date.now();
        const roomScheduleMatchKey = getRoomScheduleMatchKey(roomData);
        const surveyDocRef = firestore
          .collection('clients')
          .doc(clientId)
          .collection('surveys')
          .doc(surveyId);
        const roomsRef = surveyDocRef.collection('rooms');
        const roomDocsSnapshot = await roomsRef.get();
        const matchedRoomDocs = roomDocsSnapshot.docs.filter(
          (roomDoc) =>
            getRoomScheduleMatchKey(roomDoc.data() as RoomProps) ===
            roomScheduleMatchKey,
        );
        const selectedRoomDoc = matchedRoomDocs.find(
          (roomDoc) => roomDoc.id === roomData.id,
        );
        const selectedRoomData = selectedRoomDoc?.data() as
          | RoomProps
          | undefined;
        const selectedScheduleRoomById = survey.roomSchedule.data.find(
          (room) => isNotDeleted(room.deleted) && room.id === roomData.id,
        );
        const selectedScheduleRoom =
          selectedScheduleRoomById ||
          (!selectedRoomDoc
            ? survey.roomSchedule.data.find(
                (room) =>
                  isNotDeleted(room.deleted) &&
                  getRoomScheduleMatchKey(room) === roomScheduleMatchKey,
              )
            : undefined);
        const selectedRoomWasActive = selectedRoomDoc
          ? isNotDeleted(selectedRoomData?.deleted)
          : isNotDeleted(selectedScheduleRoom?.deleted);
        const roomDocsToProcess = matchedRoomDocs.filter((roomDoc) => {
          if (roomDoc.id === selectedRoomDoc?.id) {
            return true;
          }
          return !isNotDeleted((roomDoc.data() as RoomProps).deleted);
        });

        const batch = new BigBatch({ firestore });
        let surveyDeletedAssetsCount = 0;
        let surveyDeletedPartialsCount = 0;
        const processedRoomDocIds = new Set(
          roomDocsToProcess.map((room) => room.id),
        );
        const remainingActiveDuplicateDocs = matchedRoomDocs.filter(
          (roomDoc) =>
            !processedRoomDocIds.has(roomDoc.id) &&
            isNotDeleted((roomDoc.data() as RoomProps).deleted),
        );
        const survivingActiveDuplicateDoc = remainingActiveDuplicateDocs[0];

        console.log('[room-delete] matched room docs', {
          selectedRoomId: roomData.id,
          roomScheduleMatchKey,
          matchedRoomDocIds: matchedRoomDocs.map((roomDoc) => roomDoc.id),
          processedRoomDocIds: [...processedRoomDocIds],
          remainingActiveDuplicateRoomDocIds: remainingActiveDuplicateDocs.map(
            (roomDoc) => roomDoc.id,
          ),
          survivingActiveDuplicateRoomId: survivingActiveDuplicateDoc?.id,
        });

        for (const matchedRoomDoc of roomDocsToProcess) {
          const matchedRoomData = matchedRoomDoc.data() as RoomProps;
          let roomDeletedAssetsCount = 0;
          let roomDeletedPartialsCount = 0;
          const roomAssetDocs = await matchedRoomDoc.ref
            .collection('assets')
            .get();

          if (!roomAssetDocs.empty) {
            roomAssetDocs.forEach((assetDoc) => {
              const assetData = assetDoc.data();
              const isAssetActive = isNotDeleted(assetData.deleted);
              if (!isAssetActive) {
                return;
              }

              roomDeletedAssetsCount += 1;
              surveyDeletedAssetsCount += 1;
              let assetDeletedPartialsCount = 0;
              const updatedPartials = { ...(assetData.partials || {}) };

              Object.values(assetData.partials || {}).forEach(
                (partial: any) => {
                  if (isNotDeleted(partial?.deleted)) {
                    assetDeletedPartialsCount += 1;
                    roomDeletedPartialsCount += 1;
                    surveyDeletedPartialsCount += 1;
                    updatedPartials[partial.id] = {
                      ...partial,
                      deleted: deletedAt,
                    };
                  }
                },
              );

              const assetCounterUpdates = {
                partials: updatedPartials,
                partialsNumDeleted:
                  (assetData.partialsNumDeleted || 0) +
                  assetDeletedPartialsCount,
              };

              batch.update(assetDoc.ref, {
                deleted: deletedAt,
                ...assetCounterUpdates,
                ...getLastUpdated(),
              });
            });
          }

          console.log('[room-delete] room counter changes', {
            roomId: matchedRoomDoc.id,
            roomDeletedAssetsCount,
            roomDeletedPartialsCount,
            previousAssetsNumDeleted: matchedRoomData?.assetsNumDeleted || 0,
            previousPartialsNumDeleted:
              matchedRoomData?.partialsNumDeleted || 0,
            nextAssetsNumDeleted:
              (matchedRoomData?.assetsNumDeleted || 0) + roomDeletedAssetsCount,
            nextPartialsNumDeleted:
              (matchedRoomData?.partialsNumDeleted || 0) +
              roomDeletedPartialsCount,
            roomWasAlreadyDeleted: !isNotDeleted(matchedRoomData.deleted),
          });

          batch.update(matchedRoomDoc.ref, {
            deleted: isNotDeleted(matchedRoomData.deleted)
              ? deletedAt
              : matchedRoomData.deleted,
            assetsNumDeleted:
              (matchedRoomData?.assetsNumDeleted || 0) + roomDeletedAssetsCount,
            partialsNumDeleted:
              (matchedRoomData?.partialsNumDeleted || 0) +
              roomDeletedPartialsCount,
            ...getLastUpdated(),
          });
        }

        const matchedScheduleRoomIds = new Set<string>();
        let roomScheduleWithRoomDeleted = survey.roomSchedule.data.map(
          (room) => {
            const matchesSelectedRoom =
              room.id === selectedScheduleRoom?.id ||
              (!selectedScheduleRoom &&
                !selectedRoomDoc &&
                isNotDeleted(room.deleted) &&
                getRoomScheduleMatchKey(room) === roomScheduleMatchKey);

            if (matchesSelectedRoom && isNotDeleted(room.deleted)) {
              matchedScheduleRoomIds.add(room.id);
              return {
                ...room,
                deleted: deletedAt,
              };
            }
            return room;
          },
        );

        const hasActiveScheduleForSurvivingDuplicate =
          survivingActiveDuplicateDoc &&
          roomScheduleWithRoomDeleted.some(
            (room) =>
              isNotDeleted(room.deleted) &&
              room.id === survivingActiveDuplicateDoc.id,
          );

        if (
          survivingActiveDuplicateDoc &&
          !hasActiveScheduleForSurvivingDuplicate
        ) {
          const survivingRoomData =
            survivingActiveDuplicateDoc.data() as RoomProps;
          const survivingScheduleRow = {
            ...(selectedScheduleRoom || {}),
            areaFloor: Number(survivingRoomData.areaFloor || 0),
            areaWall: Number(survivingRoomData.areaWall || 0),
            deleted: 0,
            floor: survivingRoomData.floor,
            id: survivingActiveDuplicateDoc.id,
            name: survivingRoomData.name,
            notes: survivingRoomData.notes?.trim() || '',
          };

          roomScheduleWithRoomDeleted = [
            ...roomScheduleWithRoomDeleted,
            survivingScheduleRow,
          ];
        }

        const roomStatus = selectedRoomData?.status || roomData.status;
        const roomCounterUpdates = {
          roomsNumDeleted: firestore.FieldValue.increment(
            selectedRoomWasActive ? 1 : 0,
          ),
          roomsNumCompleted: firestore.FieldValue.increment(
            roomStatus === Statuses.COMPLETED && selectedRoomWasActive ? -1 : 0,
          ),
          roomsNumInProgress: firestore.FieldValue.increment(
            roomStatus === Statuses.IN_PROGRESS && selectedRoomWasActive
              ? -1
              : 0,
          ),
          roomsNumNoAccess: firestore.FieldValue.increment(
            roomStatus === Statuses.NO_ACCESS && selectedRoomWasActive ? -1 : 0,
          ),
          roomsNumNotStarted: firestore.FieldValue.increment(
            !selectedRoomDoc ||
              (roomStatus === Statuses.NOT_STARTED && selectedRoomWasActive)
              ? -1
              : 0,
          ),
        };

        const surveyCounterUpdates = {
          assetsNumDeleted: firestore.FieldValue.increment(
            surveyDeletedAssetsCount,
          ),
          partialsNumDeleted: firestore.FieldValue.increment(
            surveyDeletedPartialsCount,
          ),
        };

        console.log('[room-delete] survey counter changes', {
          selectedRoomId: roomData.id,
          surveyDeletedAssetsCount,
          surveyDeletedPartialsCount,
          surveyRoomCounterMode: 'FieldValue.increment',
          selectedRoomWasActive,
        });

        const floorPlanWithRoomPinRemoved = survey.floorPlans?.map((fp) => {
          if (fp.floor !== roomData.floor) {
            return fp;
          }
          return {
            ...fp,
            markers:
              fp.markers?.filter(
                (marker) =>
                  !matchedScheduleRoomIds.has(marker.roomId) &&
                  !processedRoomDocIds.has(marker.roomId),
              ) ?? [],
          };
        });

        batch.update(surveyDocRef, {
          ...surveyCounterUpdates,
          roomSchedule: {
            ...survey.roomSchedule,
            data: roomScheduleWithRoomDeleted,
          },
          floorPlans: floorPlanWithRoomPinRemoved || survey.floorPlans,
          ...roomCounterUpdates,
          ...getLastUpdated(),
        });

        await batch.commit();

        dispatch(
          setSnackMessage({ message: `${roomData.name} Successfully Deleted` }),
        );
      } catch (err) {
        dispatch(
          setSnackMessage({
            message: `There was an error deleting ${roomData.name}. Please try again.`,
          }),
        );
      } finally {
        dispatch(setLoading(false));
      }
    },
    [clientId, surveyId, survey],
  );

  const canShowSurveyorActions =
    !isCheckingAuth && checkAuth({ atLeast: PORTAL_ROLES.SURVEYOR });

  const actionButtons = useMemo(
    () =>
      function buttons({ row, closeMenu }) {
        if (!canShowSurveyorActions) {
          return null;
        }

        const buttons: React.ReactNode[] = [];

        const rowData = row.original;

        buttons.push(
          <TableActionButton
            key="View"
            text="View"
            colour="green"
            onClick={() => {
              closeMenu();
              onClickedViewRoom(rowData);
            }}
          />,
          survey.status !== Statuses.IN_PROGRESS && (
            <TableActionButton
              key="Edit"
              text="Edit"
              colour="blue"
              onClick={() => {
                closeMenu();
                onClickedEditRoom(rowData);
              }}
            />
          ),
          survey.status === Statuses.COMPLETED && (
            <TableActionButton
              key="Delete"
              text="Delete"
              colour="red"
              onClick={() => {
                closeMenu();
                onClickedDeleteRoom(rowData);
              }}
            />
          ),
        );

        return (
          <Stack direction="column" p={1}>
            {buttons}
          </Stack>
        );
      },
    [
      canShowSurveyorActions,
      onClickedEditRoom,
      onClickedViewRoom,
      onClickedDeleteRoom,
      survey.status,
    ],
  );

  const onUpdateRoom = (data) => {
    dispatch(onSaveRoom({ room: data }));
  };

  const onClickedEditFloor = () => {
    setFloorToEdit(floorName);
  };

  const onUpdateFloor = (newFloorName, prevFloorName) => {
    if (floors.find((f) => f === newFloorName)) {
      return showModal({
        title: 'Floor Already Exists!',
        messages: [`There is already a floor called ${newFloorName}`],
        buttons: {
          confirm: [{ text: 'Ok' }],
        },
      });
    }

    dispatch(onSaveFloor({ newFloorName, prevFloorName }));
  };

  if (!floorName) {
    return null;
  }

  return (
    <Paper sx={{ width: '100%' }} id="main-content">
      <ErrorBoundary>
        <AddOrEditRoomDialog
          onSubmit={onUpdateRoom}
          //@ts-ignore - needs fixing
          floors={floors}
          room={roomToAddOrEdit}
          onClose={() => setRoomToAddOrEdit(undefined)}
        />
        <EditFloorDialog
          floor={floorToEdit}
          onClose={() => setFloorToEdit(undefined)}
          onSubmit={onUpdateFloor}
        />
        {floorPlan?.url && (
          <ViewFloorPlanDialog
            open={showFloorPlan}
            floorPlan={floorPlan}
            roomsList={allRoomsForFloor}
            onClose={onToggleFloorPlan}
          />
        )}
        <Table
          rows={rows}
          columns={TABLE_COLUMNS}
          onClickRow={onClickedViewRoom}
          tableProps={{
            state: { isLoading: isRoomsLoading },
            renderRowActionMenuItems: actionButtons,
            enableRowActions: true,
            positionActionsColumn: 'first',
            renderTopToolbarCustomActions: () => {
              return (
                <Stack
                  px={1}
                  mb={1}
                  direction="row"
                  justifyContent="space-between"
                  sx={{ width: '100%' }}
                >
                  <Typography color="secondary">{floorName}</Typography>
                  <Stack
                    direction="row"
                    justifyContent="center"
                    alignItems="center"
                    spacing={2}
                  >
                    <Box flex={1}>
                      <FormControl
                        variant="filled"
                        sx={{ minWidth: 150, flex: 1 }}
                        fullWidth
                      >
                        <Autocomplete
                          value={floorName}
                          size="small"
                          disableClearable
                          options={floors}
                          onChange={(_, value) => {
                            const floorIndex = floors.findIndex(
                              (item) => item === value,
                            );
                            if (floorIndex === -1) {
                              return;
                            }
                            updateQueryParams({
                              page: 0,
                              floor: floorIndex,
                            });
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Floor"
                              variant="outlined"
                            />
                          )}
                        />
                      </FormControl>
                    </Box>
                    {survey.status !== Statuses.IN_PROGRESS &&
                    canShowSurveyorActions ? (
                      <Button
                        variant="outlined"
                        onClick={onClickedEditFloor}
                        color="secondary"
                      >
                        Edit Floor
                      </Button>
                    ) : null}

                    {floorPlan?.url ? (
                      <Button
                        variant="outlined"
                        onClick={onToggleFloorPlan}
                        color="primary"
                      >
                        View Floor Plan
                      </Button>
                    ) : null}
                    <Button
                      variant="outlined"
                      onClick={onClickExportRoomSchedule}
                      color="secondary"
                    >
                      Export Full Room Schedule
                    </Button>
                    <AssetIdSearcher />
                    {canShowSurveyorActions ? (
                      <Button
                        color="primary"
                        onClick={() => setRoomToAddOrEdit(EMPTY_ROOM)}
                        variant="contained"
                      >
                        Add a Room
                      </Button>
                    ) : null}
                  </Stack>
                </Stack>
              );
            },
          }}
        />
      </ErrorBoundary>
    </Paper>
  );
};

export default Rooms;
